import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import Row from '../../../core/layout/Row';
import Column from '../../../core/layout/Column';
import TextInput from '../../../core/form/TextInput';
import FormButtons from '../../../core/form/FormButtons';
import Switch from '../../../core/form/Switch';
import FetchSelect from '../../../core/form/FetchSelect';
import LocalFileInput from '../../../core/form/LocalFileInput';
import Select from '../../../core/form/Select';
import TooltipIcon from '../../../core/layout/TooltipIcon';
import { Roles } from '../../../core/util';

const certificateTemplateLanguageOptions = [
  {
    label: 'English',
    value: 'en',
  },
  {
    label: 'Portuguese',
    value: 'pt',
  },
  {
    label: 'Spanish',
    value: 'es',
  },
];

const CourseForm = (props) => (
  <form
    onSubmit={(event) => {
      event.preventDefault();
      props.onSubmit();
    }}
  >
    <Row>
      <Column size={2}>
        <FetchSelect
          required
          label="Academic Plan"
          value={get(props.values, 'academicPlan', '')}
          onChange={(id, data) => {
            props.onChange('academicPlan', id);
            props.onChange('academicPlanKey', data.key);
          }}
          disabled={props.submitting || props.values.id}
          errorText={get(props.errors, 'academicPlan', '')}
          url="academic-plans"
          resultTransformer={{
            text: 'name',
            value: 'id',
          }}
        />
      </Column>
      {[Roles.ADMIN, Roles.CONTENT_ADMIN].includes(localStorage.role) && (
        <Column size={3}>
          <FetchSelect
            url="distributors"
            label="Distributor"
            disabled={props.values.id || props.submitting}
            value={get(props.values, 'distributor', '')}
            onChange={(distributor) => {
              props.onChange('distributor', distributor);
            }}
            errorText={get(props.errors, 'distributor', '')}
            resultTransformer={{
              text: 'name',
              value: 'id',
            }}
          />
        </Column>
      )}
      <Column size={3}>
        <TextInput
          required
          disabled={props.submitting}
          label="Name"
          value={get(props.values, 'name', '')}
          onChange={(value) => props.onChange('name', value)}
          errorText={get(props.errors, 'name', null)}
        />
      </Column>
      <Column size={2}>
        <TextInput
          label="Order"
          required
          type="number"
          disabled={props.submitting}
          value={get(props.values, 'order', '')}
          onChange={(value) => props.onChange('order', value)}
          errorText={get(props.errors, 'order', '')}
        />
      </Column>
      <Column size={2}>
        <TextInput
          label={
            <span>
              Course Hours
              <TooltipIcon text="If completed, it will be the number of hours adopted on the certificates of students who complete the course." />
            </span>
          }
          type="number"
          minNumberValue={0}
          disabled={props.submitting}
          value={get(props.values, 'courseHours', '')}
          onChange={(value) => props.onChange('courseHours', value === '' ? '' : Number(value))}
          errorText={get(props.errors, 'courseHours', '')}
        />
      </Column>
    </Row>
    <Row>
      <Column size={2}>
        <Switch
          label="Disable First Review"
          titleOff="No"
          titleOn="Yes"
          disabled={props.submitting || get(props.values, 'id', false)}
          value={get(props.values, 'disableFirstReview', false)}
          onChange={(value) => props.onChange('disableFirstReview', value)}
          errorText={get(props.errors, 'disableFirstReview', null)}
        />
      </Column>
      <Column size={2}>
        <Switch
          label="Disable Second Review"
          titleOff="No"
          titleOn="Yes"
          disabled={props.submitting || get(props.values, 'id', false)}
          value={get(props.values, 'disableSecondReview', false)}
          onChange={(value) => props.onChange('disableSecondReview', value)}
          errorText={get(props.errors, 'disableSecondReview', null)}
        />
      </Column>
      {get(props.values, 'distributor', false) && (
        <Column size={2}>
          <LocalFileInput
            required
            label="Course icon"
            onChange={(value) => props.onChange('icon', value)}
            buttonLabel="Select icon"
            accept="image"
            value={get(props.values, 'iconUrl', null)}
            disabled={props.submitting}
            errorText={get(props.errors, 'icon', null)}
          />
        </Column>
      )}
      {!get(props.values, 'distributor', false) && (
        <Column size={3}>
          <Switch
            label="Does the course need a Certification Test?"
            titleOff="No"
            titleOn="Yes"
            disabled={props.submitting || get(props.values, 'distributor', false)}
            value={get(props.values, 'needCertification', false)}
            onChange={(value) => props.onChange('needCertification', value)}
            errorText={get(props.errors, 'needCertification', null)}
          />
        </Column>
      )}
    </Row>
    <Row>
      <Column size={4}>
        <LocalFileInput
          label={
            <span>
              Certificate HTML Template
              <TooltipIcon text="Required for certificates to be issued automatically. A template file must be provided for automatic certificate issuance." />
            </span>
          }
          onChange={(value) => {
            props.onChange('template', value);
            if (!value || value instanceof File) {
              props.onChange('certificate.templateUrl', null);
            }
          }}
          buttonLabel="Select template"
          accept="html"
          value={get(props.values, 'template', null) || get(props.values, 'certificate.templateUrl', null)}
          disabled={props.submitting}
          errorText={get(props.errors, 'certificate.templateUrl', null)}
        />
      </Column>
      <Column size={2}>
        <Select
          required
          allowClear={false}
          label="Certificate Template Language"
          value={get(props.values, 'certificate.templateLanguage', 'en')}
          onChange={(value) => props.onChange('certificate.templateLanguage', value || 'en')}
          disabled={props.submitting}
          errorText={get(props.errors, 'certificate.templateLanguage', null)}
          options={certificateTemplateLanguageOptions}
        />
      </Column>
      <Column size={4}>
        <Select
          mode="multiple"
          label={
            <span>
              Custom Required Certificate Fields
              <TooltipIcon text="Required for certificates to be issued automatically. Selected fields are validated against the student's profile; for example, if CPF is selected, only students with CPF filled in will receive the certificate automatically." />
            </span>
          }
          value={get(props.values, 'certificate.customRequiredFields', [])}
          onChange={(value) => props.onChange('certificate.customRequiredFields', value || [])}
          disabled={props.submitting}
          errorText={get(props.errors, 'certificate.customRequiredFields', null)}
          options={[
            {
              label: 'CPF',
              value: 'cpf',
            },
          ]}
        />
      </Column>
    </Row>
    <FormButtons
      confirmLabel={props.values.id ? 'Update Course' : 'Create Course'}
      isDisabled={props.submitting || !props.isDirty()}
      isSubmitting={props.submitting}
      onReset={props.onReset}
    />
  </form>
);

CourseForm.propTypes = {
  onSubmit: PropTypes.func,
  onReset: PropTypes.func,
  values: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
  submitting: PropTypes.bool,
  isDirty: PropTypes.func,
};

CourseForm.defaultProps = {
  values: {},
  errors: {},
  submitting: false,
  isDirty: () => false,
  onSubmit: () => alert('submitted'),
  onReset: () => false,
};

export default CourseForm;
